import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import OwLabel from "../../shared/OwLabel/OwLabel";
import ICatalog_Item from "../../../models/ICatalog_Item";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ICatalog from "../../../models/ICatalog";
import IOnGetCatalogsDTO from "../../../models/DTO/IOnGetCatalogsDTO";
import IOnTypeaheadCatalogItemDTO from "../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "../../../models/DTO/ITypeaheadCatalogItemDTO";
import IGetCatalogsDTO from "../../../models/DTO/IGetCatalogsDTO";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import Typeahead from "../../shared/Typeahead/Typeahead";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwButton from "../../shared/OwButton/OwButton";
import IProcessWarehouseTransferItemDTO from "../../../models/DTO/IProcessWarehouseTransferItemDTO";
import IWarehouse_Transfer from "../../../models/IWarehouse_Transfer";

type Props = {
    socket: Socket;
    transfer: IWarehouse_Transfer;
};

/**
 * Inline "add item" row for the Transfer Detail screen. Mirrors the old
 * TransferDetailAddItemSidebar, but stays visible on the page so the user can keep adding items
 * (catalog dropdown -> item typeahead -> qty) without opening a sidebar each time.
 *
 * The parent (TransferDetail) listens for WarehouseOnProcessWarehouseTransferItem and refreshes the
 * table; this row listens to the same event only to clear its own form after a save it initiated.
 */
export const TransferDetailAddItemRow = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [selectedCatalog, setSelectedCatalog] = useState<ICatalog | null>(null);
    const [selectedItem, setSelectedItem] = useState<ICatalog_Item | null>(null);
    const [foundCatalogItems, setFoundCatalogItems] = useState<ICatalog_Item[]>([]);
    const [clearTypeahead, setClearTypeahead] = useState<boolean>(false);
    const [qty, setQty] = useState(0);
    const [isSaveActive, setIsSaveActive] = useState(false);

    const canAdd = !!selectedCatalog && !!selectedItem && qty > 0;

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
        setFoundCatalogItems([]);
    };

    const getCatalogs = () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        props.socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);

        // Auto-select when there's only one catalog so the user can go straight to the item search.
        if (data.catalogs.length === 1) {
            setSelectedCatalog(data.catalogs[0]);
        }
    };

    const getCatalogItem = (name: string) => {
        if (selectedCatalog) {
            const payload: ITypeaheadCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid: selectedCatalog.guid,
                name,
                options: {
                    searchName: true,
                    searchDescription: true,
                    searchSku: true,
                    searchUpc: true,
                },
            };

            props.socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
        }
    };

    const onGetCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        setFoundCatalogItems(data.catalog_items);
    };

    const clearForm = () => {
        setSelectedItem(null);
        setQty(0);
        setClearTypeahead(true);
        setFoundCatalogItems([]);
    };

    const onProcessWarehouseTransferItem = () => {
        // Only react to saves this row initiated (the parent handles other item changes/refresh).
        setIsSaveActive((wasActive) => {
            if (wasActive) {
                clearForm();
            }

            return false;
        });
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogsOnGetCatalogs, fn: onGetCatalogs },
        { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onGetCatalogItem },
        { endpoint: EndpointsEnum.WarehouseOnProcessWarehouseTransferItem, fn: onProcessWarehouseTransferItem },
    ];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        getCatalogs();

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "catalog": {
                const catalog = catalogs.find((c) => c.guid === value) || null;
                setSelectedCatalog(catalog);
                // Item belongs to a catalog, so any prior selection is no longer valid.
                setSelectedItem(null);
                setClearTypeahead(true);
                setFoundCatalogItems([]);
                break;
            }

            case "qty":
                setQty(Number(value));
                break;
        }
    };

    const selectedItemFn = (item: ICatalog_Item) => {
        setSelectedItem(item);
    };

    const saveFn = () => {
        if (canAdd && selectedItem) {
            const payload: IProcessWarehouseTransferItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalog_item_id: selectedItem.id,
                warehouse_transfer_id: props.transfer.id,
                sent_qty: qty,
            };

            props.socket.emit(EndpointsEnum.WarehouseProcessWarehouseTransferItem, payload);

            setIsSaveActive(true);
        }
    };

    return (
        <div className={"TransferDetailAddItemRow"}>
            <div className={"row g-2 align-items-end"}>
                <div className={"col-12 col-md-2"}>
                    <OwLabel title={"catalog"} />
                    <OwSelectOption
                        fn={handleChange}
                        titleProp={"name"}
                        valueProp={"guid"}
                        options={catalogs}
                        name={"catalog"}
                        selectedOption={selectedCatalog}
                        selectedOptionProp={"guid"}
                    />
                </div>

                <div className={"col-12 col-md-3"}>
                    <OwLabel title={"item"} />
                    <Typeahead
                        isSelectableFn={() => true}
                        typeaheadApiFn={getCatalogItem}
                        titleProp={"name"}
                        subTitleProp={"sku"}
                        typeaheadItems={foundCatalogItems}
                        clearTypeahead={clearTypeahead}
                        clearTypeaheadCallback={clearTypeaheadFn}
                        selectedItemFn={selectedItemFn}
                        disabled={!selectedCatalog}
                    />
                </div>

                <div className={"col-6 col-md-1"}>
                    <OwLabel title={"qty"} />
                    <OwFnInput fn={handleChange} name={"qty"} value={qty} type={"number"} />
                </div>

                <div className={"col-auto"}>
                    <OwButton
                        title={"add"}
                        leftIconClass={"fas fa-plus"}
                        className={"btn-warning"}
                        fn={saveFn}
                        disabled={!canAdd}
                        active={isSaveActive}
                        disableOnActive={true}
                    />
                </div>
            </div>
        </div>
    );
};
